import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Activity, Search, Filter } from "lucide-react";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-cyan-100 text-cyan-800",
  delete: "bg-red-100 text-red-800",
  approve: "bg-emerald-100 text-emerald-800",
  reject: "bg-red-100 text-red-800",
  move_stage: "bg-violet-100 text-violet-800",
  upload: "bg-slate-100 text-slate-800",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/audit-logs", { params: { limit: 300 } }).then(r => setLogs(r.data)).catch(() => {});
  }, []);

  const entityTypes = Array.from(new Set(logs.map(l => l.entity_type)));
  const filtered = logs.filter(l => {
    if (filter !== "all" && l.entity_type !== filter) return false;
    if (q && !(l.actor_email || "").toLowerCase().includes(q.toLowerCase()) && !(l.action || "").includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="audit-log-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Admin</div>
        <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Audit Log</h1>
        <p className="text-sm text-slate-600 mt-1">Who did what, when — across the entire platform</p>
      </div>

      <Card className="p-3 border-slate-200 shadow-none">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input placeholder="Search by actor or action…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"/>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400"/>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entity types</SelectItem>
                {entityTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Meta</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-slate-400"><Activity className="w-8 h-8 mx-auto mb-2 text-slate-300"/>No audit entries.</td></tr>
              ) : filtered.map(l => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-${l.id}`}>
                  <td className="px-4 py-2 text-slate-600 font-mono text-xs">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{l.actor_email}</div>
                    <div className="text-[10px] text-slate-500 capitalize">{l.actor_role?.replace("_", " ")}</div>
                  </td>
                  <td className="px-4 py-2">
                    <Badge className={`${ACTION_COLOR[l.action] || "bg-slate-100 text-slate-800"} border-0 capitalize text-xs`}>{l.action}</Badge>
                  </td>
                  <td className="px-4 py-2"><span className="text-slate-600">{l.entity_type}</span> <span className="text-[11px] text-slate-400 font-mono">#{l.entity_id?.slice(-8)}</span></td>
                  <td className="px-4 py-2 text-[11px] text-slate-500 font-mono truncate max-w-xs">{JSON.stringify(l.meta || {}).slice(0, 80)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
